import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  message,
  Alert,
  Divider,
  Typography,
  Space,
  Descriptions,
  Tag,
  Result,
  Tabs,
  List,
  Badge,
  Spin,
  Modal,
  Collapse,
} from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import { vipLevelLabel, vipLevelColor } from "../../utils/plan";
import request from "../../utils/request";
import { streamJson } from "../../utils/sse";
import TaskProgress from "../../componets/common/TaskProgress";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

interface BatchMigrateStatus {
  current_user: string;
  current_status: string;
  user_data?: MigrateResponse;
  error?: string;
}

interface ProjectInfo {
  id: number;
  name: string;
  is_postchat: boolean;
  key_value: string;
  key_value_list: string[];
  refer: string;
  url_binding: boolean;
  multi_language: boolean;
}

interface MigrateResponse {
  user_info: {
    open_id: number;
    email: string;
    vip_level: number;
    vip_start: string;
    vip_end: string;
    token_balance: number;
  };
  project_info: ProjectInfo[];
  summary_count: {
    postchat_count: number;
    summaryai_count: number;
  };
}

const DataMigration: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [migrationResult, setMigrationResult] =
    useState<MigrateResponse | null>(null);
  const [migrationSuccess, setMigrationSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 批量迁移状态
  const [isBatchMigrating, setIsBatchMigrating] = useState(false);
  const [batchStatus, setBatchStatus] = useState<BatchMigrateStatus[]>([]);
  const [currentProgress, setCurrentProgress] = useState({
    total: 0,
    current: 0,
    status: "",
  });
  const [isUserDetailsVisible, setIsUserDetailsVisible] = useState(false);
  const [selectedUserData, setSelectedUserData] =
    useState<MigrateResponse | null>(null);

  const controllerRef = useRef<AbortController | null>(null);

  // 在组件卸载时关闭SSE连接
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  const handleUserMigration = async (values: any) => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/admin/migrate/user`,
        {
          suid: parseInt(values.suid, 10),
          email: values.email || undefined,
          embedding: values.embedding || false,
          force_update: values.force_update || false,
          send_email: values.send_email || false,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success(response.data.msg || "用户数据导入成功");
        setMigrationResult(response.data.data);
        setMigrationSuccess(true);
      } else {
        message.error(response.data.msg || "导入失败");
        setErrorMessage(response.data.msg || "未知错误");
      }
    } catch (error: any) {
      console.error("用户数据迁移失败:", error);
      const errorMsg = error.response?.data?.msg || "迁移失败，请稍后重试";
      message.error(errorMsg);
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setMigrationResult(null);
    setMigrationSuccess(false);
    setErrorMessage(null);
  };

  // 开始批量迁移
  const handleBatchMigration = async (values: any) => {
    if (isBatchMigrating) {
      message.warning("已有批量迁移任务正在运行");
      return;
    }

    // 重置状态
    setBatchStatus([]);
    setCurrentProgress({
      total: 0,
      current: 0,
      status: "init",
    });
    setIsBatchMigrating(true);

    try {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      controllerRef.current = new AbortController();

      // 准备请求参数
      const params = {
        embedding: values.embedding || false,
        send_email: values.send_email || false,
      };

      // 创建SSE连接
      const url = `${API_BASE_URL}/api/v3/admin/migrate/batch`;

      await streamJson<BatchMigrateStatus>(url, {
        method: "POST",
        token,
        body: JSON.stringify(params),
        signal: controllerRef.current.signal,

        onJson: (data) => {
          setBatchStatus((prev) => [data, ...prev]);

          if (data.current_status === "init" && data.current_user) {
            const match = data.current_user.match(/找到 (\d+) 个未迁移用户/);
            if (match && match[1]) {
              setCurrentProgress((prev) => ({
                ...prev,
                total: parseInt(match[1], 10),
                status: "init",
              }));
            }
          } else if (data.current_user) {
            const match = data.current_user.match(/正在迁移用户 (\d+)\/(\d+)/);
            if (match && match[1] && match[2]) {
              setCurrentProgress((prev) => ({
                ...prev,
                current: parseInt(match[1], 10),
                total: parseInt(match[2], 10),
                status: data.current_status,
              }));
            }
          }

          if (
            data.current_status === "completed" ||
            data.current_status === "stopped"
          ) {
            stopBatchMigration(true);
          }
        },

        onopen: async (response: Response) => {
          console.log("SSE连接已建立", response);
        },

        onerror: (error) => {
          console.error("SSE连接错误:", error);
          message.error("批量迁移连接出错，请重试");
          stopBatchMigration();
        },

        onclose: () => {
          console.log("SSE连接已关闭");
          if (isBatchMigrating) {
            stopBatchMigration();
          }
        },
      });
    } catch (error: any) {
      console.error("启动批量迁移失败:", error);
      message.error(error.response?.data?.msg || "启动批量迁移失败");
      stopBatchMigration();
    }
  };

  // 停止批量迁移
  const stopBatchMigration = async (isNaturalEnd = false) => {
    // 关闭SSE连接
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    if (!isNaturalEnd) {
      try {
        const response = await request.post(
          `${API_BASE_URL}/api/v3/admin/migrate/stop`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          message.success(
            response.data.msg || "迁移任务将在当前用户完成后停止"
          );
        }
      } catch (error: any) {
        console.error("停止批量迁移失败:", error);
        message.error(error.response?.data?.msg || "停止批量迁移失败");
      }
    }

    setIsBatchMigrating(false);
  };

  // 查看用户详情
  const showUserDetails = (userData: MigrateResponse) => {
    setSelectedUserData(userData);
    setIsUserDetailsVisible(true);
  };

  // 导出日志
  const exportBatchLogs = () => {
    if (batchStatus.length === 0) {
      message.warning("没有迁移日志可导出");
      return;
    }

    let logContent = "时间,用户信息,迁移状态,错误详情\n";

    // 逆序处理，保持时间顺序
    [...batchStatus].reverse().forEach((status, index) => {
      const timestamp = new Date().toLocaleString();
      const userInfo = status.current_user.replace(/,/g, ";");
      const migrationStatus = status.current_status;
      const errorInfo = status.error ? status.error.replace(/,/g, ";") : "";

      logContent += `${timestamp},${userInfo},${migrationStatus},${errorInfo}\n`;
    });

    const blob = new Blob([logContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `批量迁移日志_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case "success":
        return <Badge status="success" text="成功" />;
      case "error":
        return <Badge status="error" text="失败" />;
      case "migrating":
        return <Badge status="processing" text="迁移中" />;
      case "init":
        return <Badge status="default" text="初始化" />;
      case "stopped":
        return <Badge status="warning" text="已停止" />;
      case "completed":
        return <Badge status="success" text="全部完成" />;
      default:
        return <Badge status="default" text={status} />;
    }
  };

  const renderMigrationResult = () => {
    if (!migrationResult) return null;

    const { user_info, project_info, summary_count } = migrationResult;

    return (
      <Card variant="outlined" style={{ marginTop: "24px" }}>
        <Result
          status="success"
          title="用户数据迁移成功"
          subTitle={`已成功迁移用户 ${user_info.email}（UID: ${user_info.open_id}）的数据`}
        />

        <Divider orientation="left">用户信息</Divider>
        <Descriptions bordered column={1}>
          <Descriptions.Item label="用户ID">
            {user_info.open_id}
          </Descriptions.Item>
          <Descriptions.Item label="邮箱">{user_info.email}</Descriptions.Item>
          <Descriptions.Item label="会员等级">
            <Tag color={vipLevelColor(user_info.vip_level)}>
              {vipLevelLabel(user_info.vip_level)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="会员有效期">
            {new Date(user_info.vip_start).toLocaleDateString()} 至{" "}
            {new Date(user_info.vip_end).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label="Token余额">
            {user_info.token_balance}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">摘要统计</Divider>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="PostChat摘要数量">
            {summary_count.postchat_count}
          </Descriptions.Item>
          <Descriptions.Item label="SummaryAI摘要数量">
            {summary_count.summaryai_count}
          </Descriptions.Item>
          <Descriptions.Item label="总摘要数量">
            {summary_count.postchat_count + summary_count.summaryai_count}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">项目信息</Divider>
        {project_info.map((project, index) => (
          <Card
            key={project.id}
            type="inner"
            title={`项目 ${index + 1}: ${project.name}`}
            extra={
              <Tag color={project.is_postchat ? "blue" : "cyan"}>
                {project.is_postchat ? "PostChat" : "SummaryAI"}
              </Tag>
            }
            style={{ marginBottom: "16px" }}
          >
            <Descriptions column={1}>
              <Descriptions.Item label="项目ID">{project.id}</Descriptions.Item>
              <Descriptions.Item label="项目KEY">
                <Text copyable>{project.key_value}</Text>
              </Descriptions.Item>
              {project.key_value_list && project.key_value_list.length > 0 && (
                <Descriptions.Item label="旧系统KEY">
                  <Space direction="vertical">
                    {project.key_value_list.map((key, i) => (
                      <Text copyable key={i}>
                        {key}
                      </Text>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="绑定网站">
                {project.refer || "无"}
              </Descriptions.Item>
              <Descriptions.Item label="项目配置">
                <Space>
                  <Tag color={project.url_binding ? "blue" : "default"}>
                    {project.url_binding ? "URL绑定" : "无URL绑定"}
                  </Tag>
                  <Tag color={project.multi_language ? "blue" : "default"}>
                    {project.multi_language ? "多语言" : "中文"}
                  </Tag>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ))}

        <Button
          type="primary"
          onClick={resetForm}
          style={{ marginTop: "16px" }}
        >
          继续迁移其他用户
        </Button>
      </Card>
    );
  };

  // 渲染用户详情
  const renderUserDetails = () => {
    if (!selectedUserData) return null;

    const { user_info, project_info, summary_count } = selectedUserData;

    return (
      <Modal
        title="用户迁移详情"
        visible={isUserDetailsVisible}
        onCancel={() => setIsUserDetailsVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setIsUserDetailsVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        <Descriptions bordered column={1} style={{ marginBottom: 20 }}>
          <Descriptions.Item label="用户ID">
            {user_info.open_id}
          </Descriptions.Item>
          <Descriptions.Item label="邮箱">
            {user_info.email === "NULL" ? "未设置" : user_info.email}
          </Descriptions.Item>
          <Descriptions.Item label="会员等级">
            <Tag color={vipLevelColor(user_info.vip_level)}>
              {vipLevelLabel(user_info.vip_level)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="会员有效期">
            {new Date(user_info.vip_start).toLocaleDateString()} 至{" "}
            {new Date(user_info.vip_end).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label="Token余额">
            {user_info.token_balance}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">摘要统计</Divider>
        <Descriptions bordered column={2} style={{ marginBottom: 20 }}>
          <Descriptions.Item label="PostChat摘要数量">
            {summary_count.postchat_count}
          </Descriptions.Item>
          <Descriptions.Item label="SummaryAI摘要数量">
            {summary_count.summaryai_count}
          </Descriptions.Item>
          <Descriptions.Item label="总摘要数量">
            {summary_count.postchat_count + summary_count.summaryai_count}
          </Descriptions.Item>
        </Descriptions>

        {project_info.length > 0 ? (
          <>
            <Divider orientation="left">项目信息</Divider>
            <Collapse>
              {project_info.map((project, index) => (
                <Panel
                  key={project.id.toString()}
                  header={
                    <Space>
                      <span>{`项目 ${index + 1}: ${project.name}`}</span>
                      <Tag color={project.is_postchat ? "blue" : "cyan"}>
                        {project.is_postchat ? "PostChat" : "SummaryAI"}
                      </Tag>
                    </Space>
                  }
                >
                  <Descriptions column={1}>
                    <Descriptions.Item label="项目ID">
                      {project.id}
                    </Descriptions.Item>
                    <Descriptions.Item label="项目KEY">
                      <Text copyable>{project.key_value}</Text>
                    </Descriptions.Item>
                    {project.key_value_list &&
                      project.key_value_list.length > 0 && (
                        <Descriptions.Item label="旧系统KEY">
                          <Space direction="vertical">
                            {project.key_value_list.map((key, i) => (
                              <Text copyable key={i}>
                                {key}
                              </Text>
                            ))}
                          </Space>
                        </Descriptions.Item>
                      )}
                    <Descriptions.Item label="绑定网站">
                      {project.refer || "无"}
                    </Descriptions.Item>
                    <Descriptions.Item label="项目配置">
                      <Space>
                        <Tag color={project.url_binding ? "blue" : "default"}>
                          {project.url_binding ? "URL绑定" : "无URL绑定"}
                        </Tag>
                        <Tag
                          color={project.multi_language ? "blue" : "default"}
                        >
                          {project.multi_language ? "多语言" : "中文"}
                        </Tag>
                      </Space>
                    </Descriptions.Item>
                  </Descriptions>
                </Panel>
              ))}
            </Collapse>
          </>
        ) : (
          <Alert message="该用户没有关联项目" type="info" showIcon />
        )}
      </Modal>
    );
  };

  // 渲染批量迁移状态
  const renderBatchMigrationStatus = () => {
    if (!isBatchMigrating && batchStatus.length === 0) {
      return null;
    }

    const successfulMigrations = batchStatus.filter(
      (item) => item.current_status === "success" && item.user_data
    );

    return (
      <Card
        title="批量迁移状态"
        style={{ marginTop: 16 }}
        extra={
          <Space>
            {isBatchMigrating ? (
              <Button
                type="primary"
                danger
                onClick={() => stopBatchMigration()}
              >
                停止迁移
              </Button>
            ) : null}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={exportBatchLogs}
            >
              导出日志
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 20 }}>
          <Spin spinning={isBatchMigrating}>
            <TaskProgress
              title="批量迁移进度"
              current={currentProgress.current}
              total={currentProgress.total}
              status={currentProgress.status as any}
            />
          </Spin>
        </div>

        <List
          bordered
          dataSource={batchStatus}
          renderItem={(item) => (
            <List.Item
              extra={
                <Space>
                  {getStatusTag(item.current_status)}
                  {item.current_status === "success" && item.user_data && (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() =>
                        item.user_data && showUserDetails(item.user_data)
                      }
                    >
                      查看详情
                    </Button>
                  )}
                </Space>
              }
            >
              <List.Item.Meta
                title={item.current_user}
                description={
                  item.error && <Text type="danger">{item.error}</Text>
                }
              />
            </List.Item>
          )}
          style={{ maxHeight: 400, overflow: "auto" }}
        />

        {successfulMigrations.length > 0 && (
          <Alert
            type="success"
            message={`已成功迁移 ${successfulMigrations.length} 个用户`}
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    );
  };

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Card
        title={
          <span>
            <i
              className="ri-database-2-line"
              style={{ marginRight: "8px", color: "#1677ff" }}
            ></i>
            数据迁移
          </span>
        }
        variant="outlined"
      >
        <Tabs defaultActiveKey="single">
          <TabPane
            tab={
              <span>
                <i className="ri-user-line" style={{ marginRight: 5 }} />
                单用户迁移
              </span>
            }
            key="single"
          >
            {!migrationSuccess ? (
              <>
                <Alert
                  message="数据迁移说明"
                  description={
                    <ul style={{ paddingLeft: "20px", margin: "8px 0" }}>
                      <li>导入单个用户时，需提供旧系统的SUID</li>
                      <li>
                        导入将包含用户基本信息、会员状态、项目配置和摘要数据
                      </li>
                      <li>
                        摘要数据量较大时，会在后台异步处理，可能需要一些时间完成
                      </li>
                    </ul>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: "24px" }}
                />

                {errorMessage && (
                  <Alert
                    message="迁移错误"
                    description={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: "24px" }}
                  />
                )}

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleUserMigration}
                  initialValues={{
                    embedding: true,
                    send_email: true,
                    force_update: false,
                  }}
                >
                  <Form.Item
                    label="旧系统SUID"
                    name="suid"
                    rules={[{ required: true, message: "请输入旧系统SUID" }]}
                  >
                    <Input type="number" placeholder="请输入旧系统SUID" />
                  </Form.Item>

                  <Form.Item
                    label="邮箱（可选，若填写则优先使用此邮箱）"
                    name="email"
                  >
                    <Input placeholder="请输入用户邮箱" />
                  </Form.Item>

                  <Form.Item
                    name="embedding"
                    valuePropName="checked"
                    label="向量化处理"
                  >
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>

                  <Form.Item
                    name="force_update"
                    valuePropName="checked"
                    label="强制更新"
                  >
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>

                  <Form.Item
                    name="send_email"
                    valuePropName="checked"
                    label="发送邮件通知"
                  >
                    <Switch checkedChildren="是" unCheckedChildren="否" />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ minWidth: 100 }}
                    >
                      开始导入
                    </Button>
                  </Form.Item>
                </Form>
              </>
            ) : (
              renderMigrationResult()
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                <i className="ri-group-line" style={{ marginRight: 5 }} />
                批量迁移
              </span>
            }
            key="batch"
          >
            <Alert
              message="批量迁移说明"
              description={
                <ul style={{ paddingLeft: "20px", margin: "8px 0" }}>
                  <li>批量迁移将处理所有未迁移的SummaryAI用户</li>
                  <li>处理过程中可能需要较长时间，请保持网页连接</li>
                  <li>中断连接后，迁移也会停止</li>
                  <li>迁移日志将实时显示处理进度和结果</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginBottom: "24px" }}
            />

            <Form
              form={batchForm}
              layout="vertical"
              onFinish={handleBatchMigration}
              initialValues={{
                embedding: false,
                send_email: false,
              }}
            >
              <Form.Item
                name="embedding"
                valuePropName="checked"
                label="向量化处理"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>

              <Form.Item
                name="send_email"
                valuePropName="checked"
                label="发送邮件通知"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isBatchMigrating}
                    disabled={isBatchMigrating}
                    style={{ minWidth: 100 }}
                  >
                    开始批量迁移
                  </Button>
                  {isBatchMigrating && (
                    <Button
                      danger
                      onClick={() => stopBatchMigration()}
                      style={{ minWidth: 100 }}
                    >
                      停止迁移
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>

            {renderBatchMigrationStatus()}
          </TabPane>
        </Tabs>
      </Card>
      {renderUserDetails()}
    </div>
  );
};

export default DataMigration;
